// scheduling/components/pool/AssignModal.jsx
// Manager picks who gets a pooled job (Job Pools, Trello #257).
//
// Two tabs, straight from the POC:
//   Available Now  — staff checked in as available, LONGEST-WAITING FIRST, with
//                    their capacity signal. Rank 1 is the fair pick.
//   Push to Anyone — any eligible staffer, whether or not they've checked in.
//
// Assigning past a higher-ranked available person is allowed — that's the
// manager's call — but the server records the assignee's FIFO rank at that moment
// so the admin audit (§6) can surface habitual queue-skipping. The rank badges
// here make that visible up front rather than as a surprise in a report.
//
// Eligibility is filtered server-side for the Available tab and mirrored here for
// Push to Anyone; the server re-validates on assign either way.

import { useMemo, useState } from 'react';
import { POOL_META, isEligible } from '../../utils/poolEligibility';

function fmtWaited(iso) {
  if (!iso) return '';
  const mins = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return mins % 60 ? `${h}h ${mins % 60}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

export default function AssignModal({ job, available, staff, rules, onPick, onClose }) {
  const [tab, setTab] = useState('available');
  const [search, setSearch] = useState('');
  const poolLabel = POOL_META[job.PoolCode]?.label || job.PoolCode;

  // Push-to-Anyone candidates: eligible for this pool, excluding anyone already
  // shown on the Available tab.
  const availableIds = useMemo(() => new Set((available || []).map(a => a.StaffId)), [available]);
  const anyone = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (staff || [])
      .filter(s => !availableIds.has(s.StaffId))
      .filter(s => isEligible(s, job.PoolCode, rules))
      .filter(s => !q || (s.Name || '').toLowerCase().includes(q))
      .sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));
  }, [staff, availableIds, job.PoolCode, rules, search]);

  // Read from the live rules rather than restated, since departments and minimum level
  // are editable in the Pool Audit tab (ETF, for one, now takes GENERAL and TAX too).
  const poolRule = rules?.[job.PoolCode];
  const scopeNote = `${poolLabel} — ${poolRule?.depts?.length ? poolRule.depts.join(', ') : 'firm-wide'}`
    + `${poolRule?.minLevel ? `, ${poolRule.minLevel} and above` : ''}.`;

  // Item 3.3 — capacity is an ASSIGN-side signal, not a claim-side gate. Per the
  // requirements (§4.2) the point of the capacity number is so managers "match job
  // size to bandwidth when making direct assignments", so flag a candidate whose
  // stated free hours are less than the job's budget. Advisory only: a short
  // candidate is still fully pickable — plenty of jobs get split or run long, and
  // the manager knows things the number doesn't.
  const jobHours = Number(job.BudgetHours) || 0;

  function Row({ s, rank }) {
    const cap = s.CapacityHours == null ? null : Number(s.CapacityHours);
    const short = cap != null && jobHours > 0 && cap < jobHours;
    return (
      <button type="button" className="sched-pool-assign-row" onClick={() => onPick(s)}>
        {rank != null && (
          <span className={`sched-pool-assign-rank${rank === 1 ? ' sched-pool-assign-rank--first' : ''}`}>{rank}</span>
        )}
        <span className="sched-pool-assign-info">
          <span className="sched-pool-assign-name">{s.Name}</span>
          <span className="sched-pool-assign-sub">
            {s.Level}{s.DepartmentCode ? ` · ${s.DepartmentCode}` : ''}
            {s.CheckedInAt && <span className="sched-pool-assign-free"> · free {fmtWaited(s.CheckedInAt)}</span>}
          </span>
        </span>
        {cap != null && (
          <span
            className={`sched-pool-assign-cap${short ? ' sched-pool-assign-cap--short' : ''}`}
            title={short
              ? `Says they have ${Math.round(cap * 100) / 100}h free, but this job is budgeted at ${Math.round(jobHours * 100) / 100}h — you can still assign it`
              : `Marked themselves available for ${Math.round(cap * 100) / 100}h`}
          >
            {Math.round(cap * 100) / 100}h free{short ? ` · under ${Math.round(jobHours * 100) / 100}h job` : ''}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="sched-mgr-modal-backdrop" onClick={onClose}>
      <div className="sched-mgr-modal" onClick={e => e.stopPropagation()}>
        <div className="sched-mgr-modal-header">
          <h3>Assign Job</h3>
          <button className="sched-mgr-sidebar-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div className="sched-mgr-modal-job">
          <span className="sched-mgr-modal-job-name">{job.JobName}</span>
          <span className="sched-mgr-modal-job-client">{job.ClientName}</span>
          <span className="sched-mgr-modal-job-budget">
            {job.ServiceDesc || job.JobType || 'General'} · {Math.round((Number(job.BudgetHours) || 0) * 100) / 100}h · {poolLabel} pool
          </span>
        </div>

        <div className="sched-modal-picklist-toggle">
          <button
            type="button"
            className={`sched-modal-picklist-tab${tab === 'available' ? ' sched-modal-picklist-tab--on' : ''}`}
            onClick={() => setTab('available')}
          >
            Available Now ({(available || []).length})
          </button>
          <button
            type="button"
            className={`sched-modal-picklist-tab${tab === 'anyone' ? ' sched-modal-picklist-tab--on' : ''}`}
            onClick={() => setTab('anyone')}
          >
            Push to Anyone
          </button>
        </div>

        <div className="sched-modal-picklist-note">
          {tab === 'available'
            ? `Checked-in staff, longest-waiting first. ${scopeNote}`
            : `Assign directly to any eligible staffer, checked in or not. ${scopeNote}`}
        </div>

        {tab === 'available' ? (
          (available || []).length === 0 ? (
            <div className="sched-modal-picklist-empty">
              Nobody is checked in as available for this pool.<br />
              Use <strong>Push to Anyone</strong> to assign directly.
            </div>
          ) : (
            <div className="sched-pool-assign-list">
              {available.map((s, i) => <Row key={s.StaffId} s={s} rank={i + 1} />)}
            </div>
          )
        ) : (
          <>
            <input
              className="sched-modal-job-search"
              placeholder="Search by name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {anyone.length === 0 ? (
              <div className="sched-modal-picklist-empty">No eligible staff match.</div>
            ) : (
              <div className="sched-pool-assign-list">
                {anyone.map(s => <Row key={s.StaffId} s={s} />)}
              </div>
            )}
          </>
        )}

        <div className="sched-pool-footnote">
          Assigning past someone who has been waiting longer is allowed, but the
          assignee&rsquo;s queue position is recorded for the admin FIFO audit.
        </div>
      </div>
    </div>
  );
}
